/**
 * PDP HTML Parser (TRAC-291)
 *
 * Extracts tire, size, and vehicle context from raw TireRack.com PDP HTML.
 * TR.com only sends raw HTML via postMessage — no structured data.
 *
 * Extraction points (verified against all 4 PDP page variants):
 *   Tire:    <title> tag — everything before the size pattern or "| Tire Rack"
 *   Size:    "Selected Size: <span>XXX/XXRXX" element
 *   Vehicle: <span id="vehicleNavMakeModel"> text content
 */

import type { PageContext } from '../services/RealChatAgentService';

/** Parse a tire size string into width/ratio/diameter.
 *  Handles: P215/55R17, LT265/70R18, 235/65R16C, ST235/75R15, T125/70R18,
 *           245/35ZR19, 215/55R17 (Euro-metric, no prefix)
 */
function parseSize(sizeStr: string): { display: string; width: string; ratio: string; diameter: string } | null {
  // Match optional prefix (LT, P, ST, T) + width/ratio + optional ZR or R + diameter + optional C suffix
  const match = sizeStr.match(/(?:LT|ST|P|T)?(\d{2,3})\/(\d{2,3})Z?[RD](\d{2})C?/);
  if (!match) return null;
  return { display: match[0], width: match[1], ratio: match[2], diameter: match[3] };
}

/**
 * Parse a vehicle string like "2008 Toyota Sienna LE FWD" into year/make/rest.
 * The "model" from TR.com includes the trim (e.g., "Sienna LE FWD").
 */
function parseVehicle(vehicleStr: string): { year: string; make: string; model: string } | null {
  const match = vehicleStr.match(/^(\d{4})\s+(\S+)\s+(.+)$/);
  if (!match) return null;
  return { year: match[1], make: match[2], model: match[3].trim() };
}

/** Tire size pattern in dash format: 275/65-18, 235/40-19, LT265/70-18 */
const DASH_SIZE_RE = /(?:LT|P)?(\d{2,3})\/(\d{2,3})-(\d{2})/;

/** Convert dash format "275/65-18" → "275/65R18" */
function dashToStandard(dashSize: string): string {
  return dashSize.replace(/^((?:LT|P)?\d{2,3}\/\d{2,3})-(\d{2})/, '$1R$2');
}

/**
 * Parse autoModClar which can contain tire sizes, trims, or both.
 *
 * Patterns:
 *   "275/65-18"                     → { tireSize: "275/65R18" }
 *   "275/45-21F 315/40-21R"         → { frontSize: "275/45R21", rearSize: "315/40R21", tireSize: "F: 275/45R21 / R: 315/40R21" }
 *   "275/50-20, Runflat Tire"       → { tireSize: "275/50R20", trim: "Runflat Tire" }
 *   "Sport Brakes, Runflat Tire"    → { trim: "Sport Brakes, Runflat Tire" }
 *   "Crew Cab"                      → { trim: "Crew Cab" }
 */
function parseModClar(modClar: string): {
  tireSize?: string;
  frontSize?: string;
  rearSize?: string;
  trim?: string;
} {
  // Staggered: "275/45-21F 315/40-21R"
  const staggeredMatch = modClar.match(
    /^((?:LT|P)?\d{2,3}\/\d{2,3}-\d{2})F\s+((?:LT|P)?\d{2,3}\/\d{2,3}-\d{2})R$/
  );
  if (staggeredMatch) {
    const front = dashToStandard(staggeredMatch[1]);
    const rear = dashToStandard(staggeredMatch[2]);
    return { frontSize: front, rearSize: rear, tireSize: `F: ${front} / R: ${rear}` };
  }

  // Size + trim: "275/50-20, Runflat Tire"
  const sizeWithTrimMatch = modClar.match(
    /^((?:LT|P)?\d{2,3}\/\d{2,3}-\d{2}),\s*(.+)$/
  );
  if (sizeWithTrimMatch) {
    return { tireSize: dashToStandard(sizeWithTrimMatch[1]), trim: sizeWithTrimMatch[2].trim() };
  }

  // Simple tire size: "275/65-18" or "235/40-19"
  if (DASH_SIZE_RE.test(modClar) && modClar.trim().match(/^(?:LT|P)?\d{2,3}\/\d{2,3}-\d{2}$/)) {
    return { tireSize: dashToStandard(modClar.trim()) };
  }

  // Pure trim: "Sport Brakes", "Crew Cab", "Sport Brakes, Runflat Tire"
  return { trim: modClar };
}

/**
 * Extract tire name, size, and vehicle from raw PDP HTML.
 *
 * Returns a PageContext with whatever fields are present on the page.
 * Missing fields are left undefined (not all pages have size or vehicle).
 */
export function parsePdpHtml(html: string): PageContext | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const context: PageContext = {};
  let foundAnything = false;

  // --- Tire: from <title> tag ---
  const title = doc.querySelector('title')?.textContent?.replace(/\u00a0/g, ' ').trim();
  if (title) {
    // Title formats:
    //   "Goodyear Assurance WeatherReady2 215/65R16"  (has size)
    //   "Michelin Pilot Alpin PA4 | Tire Rack"         (no size)
    //   "Goodyear Assurance MaxLife2 for 2017 Toyota... | Tire Rack"  (has vehicle)
    let tireName = title
      .replace(/\s*\|.*$/, '')          // strip "| Tire Rack"
      .replace(/\s+for\s+\d{4}\s+.*$/, '')  // strip "for 2017 Toyota..."
      .replace(/\s*(?:LT|ST|P|T)?\d{2,3}\/\d{2,3}Z?[RD]\d{2}C?.*$/, '')  // strip size (all formats)
      .trim();

    if (tireName) {
      context.tire_name = tireName;

      // Split brand (first word) from model (rest)
      const spaceIdx = tireName.indexOf(' ');
      if (spaceIdx > 0) {
        context.tire_brand = tireName.substring(0, spaceIdx);
        context.tire_model = tireName.substring(spaceIdx + 1);
      }
      foundAnything = true;
    }
  }

  // --- Size: from "Selected Size: <span>" ---
  // Handles metric (215/55R17), flotation (37X12.5R17LT), and staggered (front/rear from URL params)
  const sizeMatch = html.match(/Selected Size:\s*<span[^>]*>([^<]+)/i);
  if (sizeMatch) {
    const rawSize = sizeMatch[1].trim();
    // Try metric first (215/55R17)
    const parsed = parseSize(rawSize);
    if (parsed) {
      context.product_size = parsed.display;
      foundAnything = true;
    } else {
      // Try flotation (37X12.5R17LT, 33x12.50R17)
      const flotMatch = rawSize.match(/(\d{2,3})[xX](\d+\.?\d*)[rR](\d{2})/);
      if (flotMatch) {
        context.product_size = flotMatch[0].toUpperCase();
        foundAnything = true;
      }
    }

    // Detect whether this size fits the user's vehicle.
    // TR.com shows "This size fits your [vehicle]" or "We have not confirmed..."
    if (context.product_size) {
      const fitsMatch = html.match(/This size fits your\s/i);
      const notConfirmedMatch = html.match(/not confirmed that this size fits/i);
      const notAvailableMatch = html.match(/Tire is Not Available for Your Vehicle/i);

      if (fitsMatch && !notConfirmedMatch && !notAvailableMatch) {
        context.size_fits_vehicle = true;
        context.vehicle_size = context.product_size;
      } else if (notConfirmedMatch || notAvailableMatch) {
        context.size_fits_vehicle = false;
        // vehicle_size is NOT set here — it will be carried forward
        // from a previous page context by setPageContext() if the vehicle
        // hasn't changed.
      }
    }
  }

  // Staggered fitment: try multiple extraction strategies
  // Strategy 1: frontWidth/rearWidth URL params (e.g., BMW M4 page)
  // Strategy 2: Title tag pattern "255/35R19 - diameter Front | 275/35R19 - diameter Rear" (e.g., Supra, BMW 330i)
  if (!context.product_size) {
    let front: string | null = null;
    let rear: string | null = null;

    // Strategy 1: URL params
    const frontWidthMatch = html.match(/frontWidth[=:](\d{2,3})(?:%2F|%252F|\/|X)/i);
    const frontRatioMatch = html.match(/frontRatio[=:](\d{1,3}(?:\.\d+)?)/i);
    const frontDiameterMatch = html.match(/frontDiameter[=:](\d{2})/i);
    const rearWidthMatch = html.match(/rearWidth[=:](\d{2,3})(?:%2F|%252F|\/|X)/i);
    const rearRatioMatch = html.match(/rearRatio[=:](\d{1,3}(?:\.\d+)?)/i);
    const rearDiameterMatch = html.match(/rearDiameter[=:](\d{2})/i);

    if (frontWidthMatch && frontRatioMatch && frontDiameterMatch &&
        rearWidthMatch && rearRatioMatch && rearDiameterMatch) {
      front = `${frontWidthMatch[1]}/${frontRatioMatch[1]}R${frontDiameterMatch[1]}`;
      rear = `${rearWidthMatch[1]}/${rearRatioMatch[1]}R${rearDiameterMatch[1]}`;
    }

    // Strategy 2: Title tag "XXX/XXR19 - diameter Front | XXX/XXR19 - diameter Rear"
    if (!front && title) {
      const titleStaggered = title.match(/((?:LT|ST|P|T)?\d{2,3}\/\d{2,3}Z?[RD]\d{2}C?)\s*-\s*(?:diameter\s*)?Front\s*\|\s*((?:LT|ST|P|T)?\d{2,3}\/\d{2,3}Z?[RD]\d{2}C?)\s*-\s*(?:diameter\s*)?Rear/i);
      if (titleStaggered) {
        front = titleStaggered[1];
        rear = titleStaggered[2];
      }
    }

    if (front && rear) {
      context.product_size = `F: ${front} / R: ${rear}`;
      foundAnything = true;
    }
  }

  // --- Vehicle: prefer autoModel/autoModClar URL params from the page ---
  // TireRack pages embed the correct model/trim split in links and hidden inputs.
  // Extract from hidden inputs first, then fall back to URL params in href/data-url.
  const autoModelInput = doc.querySelector('input[name="autoModel"]') as HTMLInputElement | null;
  const autoModClarInput = doc.querySelector('input[name="autoModClar"]') as HTMLInputElement | null;

  let autoModel = autoModelInput?.value?.trim() || null;
  let autoModClar = autoModClarInput?.value?.trim() || null;
  let autoMake: string | null = null;
  let autoYear: string | null = null;

  // Fall back to extracting from URL params in the HTML (href or data-url attributes)
  if (!autoModel) {
    const urlParamMatch = html.match(/autoModel=([\w%+.-]+)/);
    if (urlParamMatch) {
      autoModel = decodeURIComponent(urlParamMatch[1].replace(/\+/g, ' ')).trim();
    }
    const modClarMatch = html.match(/autoModClar=([\w%+.-]*)/);
    if (modClarMatch) {
      autoModClar = decodeURIComponent(modClarMatch[1].replace(/\+/g, ' ')).trim() || null;
    }
  }

  // Get year and make from hidden inputs or URL params
  const autoMakeInput = doc.querySelector('input[name="autoMake"]') as HTMLInputElement | null;
  const autoYearInput = doc.querySelector('input[name="autoYear"]') as HTMLInputElement | null;
  autoMake = autoMakeInput?.value?.trim() || null;
  autoYear = autoYearInput?.value?.trim() || null;

  if (!autoMake) {
    const makeMatch = html.match(/autoMake=([\w%+.-]+)/);
    if (makeMatch) autoMake = decodeURIComponent(makeMatch[1].replace(/\+/g, ' ')).trim();
  }
  if (!autoYear) {
    const yearMatch = html.match(/autoYear=([\w%+.-]+)/);
    if (yearMatch) autoYear = decodeURIComponent(yearMatch[1].replace(/\+/g, ' ')).trim();
  }

  if (autoYear && autoMake && autoModel) {
    context.vehicle_year = autoYear;
    context.vehicle_make = autoMake;
    context.vehicle_model = autoModel;
    if (autoModClar) {
      // TR.com uses autoModClar for trim, tire size variants, or combinations:
      //   "XLE"                           → trim only
      //   "275/65-18"                     → single tire size
      //   "235/40-19"                     → single tire size (Tesla)
      //   "275/45-21F 315/40-21R"         → staggered front/rear sizes
      //   "275/50-20, Runflat Tire"       → tire size + trim combined
      //   "Sport Brakes, Runflat Tire"    → trim only (multiple)
      //   "Crew Cab"                      → trim only
      const parsed = parseModClar(autoModClar);
      if (parsed.tireSize) context.vehicle_size = parsed.tireSize;
      if (parsed.trim) context.vehicle_trim = parsed.trim;
    }
    foundAnything = true;
  } else {
    // Final fallback: parse from vehicleNavMakeModel span (no trim split possible)
    const vehicleSpan = doc.querySelector('#vehicleNavMakeModel');
    const vehicleText = vehicleSpan?.textContent?.trim();
    if (vehicleText) {
      const parsed = parseVehicle(vehicleText);
      if (parsed) {
        context.vehicle_year = parsed.year;
        context.vehicle_make = parsed.make;
        context.vehicle_model = parsed.model;
        foundAnything = true;
      }
    }
  }

  // --- Page name: from s.pageName assignment in analytics script ---
  // Pattern: s.pageName="tr: tires: product details" (consistent across all PDP pages)
  const pageNameMatch = html.match(/s\.pageName\s*=\s*"([^"]+)"/);
  if (pageNameMatch) {
    context.page_name = pageNameMatch[1];
    foundAnything = true;
  }

  return foundAnything ? context : null;
}
